import { useState } from 'react';
import { IonIcon, IonPopover } from '@ionic/react';
import { informationCircleOutline, personOutline } from 'ionicons/icons';
import type { GetMatchQuery } from '../../../../types/graphql-generated';
import { PlayerAvatar } from '../../../components/PlayerAvatar';
import { FantasyScore } from '../../../components/FantasyScore';
import { GoalIcon, PenaltyGoalIcon, OwnGoalIcon, CleanSheetIcon } from '../../../components/ui/events';
import { ExtendedPlayerInfoModal } from '../../../components/ExtendedPlayerInfoModal';

type MatchPlayer = NonNullable<GetMatchQuery['getMatch']>['matchPlayers'][number];

interface MatchPlayerCardProps {
  matchPlayer: MatchPlayer;
}

const iconBtn =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-[#e4e8f0] bg-white text-slate-400 transition-colors hover:bg-slate-50';

export const MatchPlayerCard: React.FC<MatchPlayerCardProps> = ({ matchPlayer }) => {
  const { player, minutesPlayed, goals, penaltyGoals, ownGoals, yellowCards, redCard, teamGoals, rivalGoals, fantasyScore } =
    matchPlayer;

  const [showExtendedInfo, setShowExtendedInfo] = useState(false);
  const [showPerformancePopover, setShowPerformancePopover] = useState(false);
  const [performanceEvent, setPerformanceEvent] = useState<MouseEvent | undefined>(undefined);
  const [performanceSide, setPerformanceSide] = useState<'top' | 'bottom'>('bottom');

  const hasCleanSheet = rivalGoals === 0 && minutesPlayed >= 60;

  const openPerformancePopover = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const triggerBounds = event.currentTarget.getBoundingClientRect();
    const triggerCenter = triggerBounds.top + triggerBounds.height / 2;
    setPerformanceSide(triggerCenter < window.innerHeight / 2 ? 'bottom' : 'top');
    setPerformanceEvent(event.nativeEvent);
    setShowPerformancePopover(true);
  };

  const closePerformancePopover = () => {
    setPerformanceEvent(undefined);
    setShowPerformancePopover(false);
  };

  return (
    <>
      <div className="match-player-card flex items-center gap-3 border-b border-[#eef1f6] px-3.5 py-2.5 last:border-b-0">
        <PlayerAvatar image={player.image} nick={player.nick} size="sm" />

        <div className="min-w-0 flex-1 truncate text-[14.5px] font-semibold text-[#141b2b]">
          {player.nick}
        </div>

        {fantasyScore != null && <FantasyScore value={fantasyScore} />}

        <button
          type="button"
          aria-label={`Ver rendimiento de ${player.nick}`}
          className={iconBtn}
          onClick={openPerformancePopover}
        >
          <IonIcon icon={informationCircleOutline} className="text-[17px]" />
        </button>
        <button
          type="button"
          aria-label={`Ver ficha de ${player.nick}`}
          className={iconBtn}
          onClick={() => setShowExtendedInfo(true)}
        >
          <IonIcon icon={personOutline} className="text-[17px]" />
        </button>
      </div>

      {showPerformancePopover && (
        <IonPopover
          isOpen={showPerformancePopover}
          event={performanceEvent}
          onDidDismiss={closePerformancePopover}
          className="match-player-performance-popover"
          side={performanceSide}
          alignment="center"
          showBackdrop
          backdropDismiss
        >
          <div
            role="dialog"
            aria-label={`Rendimiento de ${player.nick}`}
            className="match-player-performance-content"
          >
            <div className="mb-3 flex items-center gap-2">
              <span className="font-heading text-[15px] font-extrabold text-[#141b2b]">Rendimiento</span>
              <span className="truncate text-[12.5px] text-slate-400">· {player.nick}</span>
              {fantasyScore != null && (
                <span className="ml-auto">
                  <FantasyScore value={fantasyScore} />
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {typeof teamGoals === 'number' && typeof rivalGoals === 'number' && (
                <div className="col-span-2 flex items-center justify-between gap-2 rounded-xl bg-[#eef3fb] px-3 py-2.5">
                  <span className="text-[12.5px] font-semibold text-slate-600">Resultado del jugador</span>
                  <strong className="font-heading text-[15px] text-[#0a2a5b]">{teamGoals} - {rivalGoals}</strong>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 rounded-xl bg-[#f6f8fb] px-3 py-2.5">
                <span className="text-[12.5px] font-semibold text-slate-600">Minutos</span>
                <strong className="font-heading text-[14px]">{minutesPlayed}'</strong>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-xl bg-[#f6f8fb] px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600">
                  <GoalIcon />Goles
                </span>
                <strong className="font-heading text-[14px]">{goals}</strong>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-xl bg-[#f6f8fb] px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600">
                  <PenaltyGoalIcon />Penaltis
                </span>
                <strong className="font-heading text-[14px]">{penaltyGoals}</strong>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-xl bg-[#f6f8fb] px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600">
                  <OwnGoalIcon />Goles en propia
                </span>
                <strong className="font-heading text-[14px]">{ownGoals}</strong>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-xl bg-[#f6f8fb] px-3 py-2.5">
                <span className="text-[12.5px] font-semibold text-slate-600">Amarillas</span>
                <strong className="font-heading text-[14px]">{yellowCards}</strong>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-xl bg-[#f6f8fb] px-3 py-2.5">
                <span className="text-[12.5px] font-semibold text-slate-600">Roja</span>
                <strong className="font-heading text-[14px]">{redCard ? 'Sí' : 'No'}</strong>
              </div>
              <div className="col-span-2 flex items-center justify-between gap-2 rounded-xl bg-[#f6f8fb] px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600">
                  {hasCleanSheet && <CleanSheetIcon />}Portería a cero
                </span>
                <strong className="font-heading text-[14px]">{hasCleanSheet ? 'Sí' : 'No'}</strong>
              </div>
            </div>
          </div>
        </IonPopover>
      )}

      <ExtendedPlayerInfoModal
        isOpen={showExtendedInfo}
        onClose={() => setShowExtendedInfo(false)}
        playerId={player.id}
      />
    </>
  );
};
